import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { DashboardLayout } from "@/components/DashboardLayout";
import { getNav } from "@/lib/navConfig";
import { useAuth } from "@/context/AuthContext";
import api, { money } from "@/lib/api";
import { StatusBadge, UrgencyBadge } from "@/components/StatusBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { WO_STATUSES, SERVICE_CATEGORIES, URGENCIES, US_STATES } from "@/lib/constants";
import { Button } from "@/components/ui/button";
import { CreateWorkOrderDialog } from "@/components/CreateWorkOrderDialog";
import { AssignContractorDialog } from "@/components/AssignContractorDialog";
import { Loader2, ClipboardList, Filter, Plus, UserPlus, X, CalendarClock, ListChecks } from "lucide-react";
import { toast } from "sonner";

export default function DispatchWorkOrders() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [sp] = useSearchParams();
  const [wos, setWos] = useState(null);
  const [filters, setFilters] = useState({ status: sp.get("status") || "", urgency: "", trade: "", state: "" });
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);
  const [selected, setSelected] = useState([]);
  const [assignWos, setAssignWos] = useState(null); // array for the assign modal
  const [statusOpen, setStatusOpen] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);

  const load = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v) params[k] = v; });
    api.get("/work-orders", { params }).then(({ data }) => setWos(data)).catch(() => setWos([]));
  };
  useEffect(() => { load(); }, [filters]);

  const shown = (wos || []).filter((w) => !search || w.wo_number.toLowerCase().includes(search.toLowerCase()) || (w.property?.name || "").toLowerCase().includes(search.toLowerCase()));
  const allSelected = shown.length > 0 && selected.length === shown.length;
  const toggle = (id) => setSelected((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const toggleAll = () => setSelected(allSelected ? [] : shown.map((w) => w.id));
  const clearSel = () => setSelected([]);
  const selectedWos = shown.filter((w) => selected.includes(w.id));

  const afterAction = () => { clearSel(); load(); };

  const FSelect = ({ k, placeholder, options }) => (
    <Select value={filters[k] || "all"} onValueChange={(v) => setFilters({ ...filters, [k]: v === "all" ? "" : v })}>
      <SelectTrigger data-testid={`filter-${k}`} className="w-full sm:w-40"><SelectValue placeholder={placeholder} /></SelectTrigger>
      <SelectContent className="max-h-64"><SelectItem value="all">All {placeholder}</SelectItem>{options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
    </Select>
  );

  return (
    <DashboardLayout nav={getNav(user.role)} title="Work Orders">
      <div className="flex justify-end mb-4">
        <Button data-testid="create-wo-btn" onClick={() => setCreateOpen(true)} className="bg-blue-600 hover:bg-blue-700">
          <Plus className="w-4 h-4 mr-1.5" /> Create Work Order
        </Button>
      </div>
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-5">
        <div className="flex items-center gap-2 mb-3 text-sm font-semibold text-slate-600"><Filter className="w-4 h-4" /> Filters</div>
        <div className="flex flex-wrap gap-2">
          <Input data-testid="search-wo" placeholder="Search WO# or property" value={search} onChange={(e) => setSearch(e.target.value)} className="w-full sm:w-52" />
          <FSelect k="status" placeholder="Status" options={WO_STATUSES} />
          <FSelect k="urgency" placeholder="Urgency" options={URGENCIES} />
          <FSelect k="trade" placeholder="Trade" options={SERVICE_CATEGORIES} />
          <FSelect k="state" placeholder="State" options={US_STATES} />
        </div>
      </div>

      {/* Select-all + bulk toolbar */}
      {shown.length > 0 && (
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
            <Checkbox checked={allSelected} onCheckedChange={toggleAll} data-testid="select-all-wo" />
            Select All {selected.length > 0 && <span className="text-slate-400">({selected.length} selected)</span>}
          </label>
          {selected.length > 0 && (
            <div className="flex items-center gap-2 flex-wrap" data-testid="bulk-toolbar">
              <Button size="sm" data-testid="bulk-assign-btn" onClick={() => setAssignWos(selectedWos)} className="bg-blue-600 hover:bg-blue-700"><UserPlus className="w-4 h-4 mr-1" /> Assign Contractor</Button>
              <Button size="sm" variant="outline" data-testid="bulk-status-btn" onClick={() => setStatusOpen(true)}><ListChecks className="w-4 h-4 mr-1" /> Change Status</Button>
              <Button size="sm" variant="outline" data-testid="bulk-schedule-btn" onClick={() => setScheduleOpen(true)}><CalendarClock className="w-4 h-4 mr-1" /> Schedule</Button>
              <Button size="sm" variant="ghost" data-testid="clear-selection-btn" onClick={clearSel}><X className="w-4 h-4 mr-1" /> Clear</Button>
            </div>
          )}
        </div>
      )}

      {!wos ? <div className="flex justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-blue-600" /></div> :
        wos.length === 0 ? (
          <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="wo-empty-state">
            <ClipboardList className="w-12 h-12 text-slate-300 mx-auto" />
            <h3 className="mt-4 font-head text-lg font-semibold text-slate-900">No work orders yet</h3>
            <p className="mt-1 text-slate-500">Create your first work order to get started.</p>
            <Button data-testid="empty-create-wo-btn" onClick={() => setCreateOpen(true)} className="mt-5 bg-blue-600 hover:bg-blue-700"><Plus className="w-4 h-4 mr-1.5" /> Create Work Order</Button>
          </div>
        ) :
        shown.length === 0 ? <div className="text-center py-20 bg-white rounded-xl border border-slate-200" data-testid="wo-no-match"><ClipboardList className="w-10 h-10 text-slate-300 mx-auto" /><p className="mt-3 text-slate-500">No work orders match your filters.</p></div> : (
          <div className="space-y-2">
            {shown.map((w) => (
              <div key={w.id} data-testid={`dispatch-wo-${w.id}`} className={`flex items-center gap-3 bg-white border rounded-xl p-4 transition-colors ${selected.includes(w.id) ? "border-blue-400 bg-blue-50/40" : "border-slate-200 hover:border-blue-300"}`}>
                <Checkbox checked={selected.includes(w.id)} onCheckedChange={() => toggle(w.id)} data-testid={`select-wo-${w.id}`} />
                <button onClick={() => navigate(`/app/wo/${w.id}`)} className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 flex-wrap"><span className="font-medium text-slate-900">{w.wo_number}</span><UrgencyBadge urgency={w.urgency} /></div>
                  <div className="text-sm text-slate-500 mt-0.5 truncate">{w.service_category} · {w.property?.name} · {w.property?.city}, {w.property?.state}</div>
                  <div className="text-xs text-slate-400 mt-0.5">{w.contractor ? `Contractor: ${w.contractor.first_name} ${w.contractor.last_name}` : "Unassigned"} · Client: {w.client?.company_name}</div>
                </button>
                <div className="text-right shrink-0 flex flex-col items-end gap-1.5">
                  <StatusBadge status={w.status} />
                  {w.customer_price > 0 && <div className="text-sm font-semibold text-green-600">GP {money(w.gross_profit)} · {w.gross_margin}%</div>}
                  <Button size="sm" variant="outline" data-testid={`quick-assign-${w.id}`} onClick={() => setAssignWos([w])} className="h-7 text-xs border-blue-200 text-blue-700 hover:bg-blue-50">
                    <UserPlus className="w-3.5 h-3.5 mr-1" /> {w.assigned_contractor_id ? "Reassign" : "Assign"} Contractor
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}

      <CreateWorkOrderDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={load} />
      {assignWos && <AssignContractorDialog open={!!assignWos} onOpenChange={(v) => !v && setAssignWos(null)} workOrders={assignWos} onDone={afterAction} />}
      <BulkStatusDialog open={statusOpen} onOpenChange={setStatusOpen} ids={selected} onDone={afterAction} />
      <BulkScheduleDialog open={scheduleOpen} onOpenChange={setScheduleOpen} ids={selected} onDone={afterAction} />
    </DashboardLayout>
  );
}

function BulkStatusDialog({ open, onOpenChange, ids, onDone }) {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!status) return toast.error("Select a status");
    setBusy(true);
    try {
      const { data } = await api.post("/work-orders/bulk-assign", { work_order_ids: ids, status });
      toast.success(`${data.assigned} work orders updated.`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error("Failed to update status"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-status-modal">
        <DialogHeader><DialogTitle>Change Status — {ids.length} Work Orders</DialogTitle><DialogDescription>Apply a new status to all selected work orders.</DialogDescription></DialogHeader>
        <div><Label>New Status</Label>
          <Select value={status} onValueChange={setStatus}><SelectTrigger data-testid="bulk-status-select" className="mt-1.5"><SelectValue placeholder="Select status" /></SelectTrigger>
            <SelectContent className="max-h-64">{WO_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent></Select></div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-status-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function BulkScheduleDialog({ open, onOpenChange, ids, onDone }) {
  const [date, setDate] = useState("");
  const [time, setTime] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!date) return toast.error("Select a date");
    setBusy(true);
    try {
      const { data } = await api.post("/work-orders/bulk-assign", { work_order_ids: ids, scheduled_date: date, scheduled_time: time || null });
      toast.success(`${data.assigned} work orders scheduled.`);
      onOpenChange(false); onDone();
    } catch (e) { toast.error("Failed to schedule"); }
    setBusy(false);
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="bulk-schedule-modal">
        <DialogHeader><DialogTitle>Schedule — {ids.length} Work Orders</DialogTitle><DialogDescription>Set a scheduled date and time for all selected work orders.</DialogDescription></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Date</Label><Input data-testid="bulk-schedule-date" type="date" value={date} onChange={(e) => setDate(e.target.value)} className="mt-1.5" /></div>
          <div><Label>Time</Label><Input data-testid="bulk-schedule-time" type="time" value={time} onChange={(e) => setTime(e.target.value)} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button data-testid="bulk-schedule-submit" onClick={submit} disabled={busy} className="bg-blue-600 hover:bg-blue-700">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : "Apply"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
